// Global navigation — arched bar at the bottom of every primary surface.
//
// Ryan-direct (2026-06-07 PM): "I want fixed navigation everywhere — the
// arched navigation bar." Replaces the flat LIBRARY · SCRIPT · RUN LINES ·
// KIT text row with the arched mechanic ported from MagicPatterns
// RehearsalRoomScreen.tsx — soft arc baseline, four-room track that
// translates under a fixed pointer-dot, swipe to step, tap the dot to
// navigate.
//
// Hidden on the upload flow (welcome / quiz / upload itself) where the
// in-screen affordances ARE the navigation, and on /room because the
// Rehearsal Room screen has its own internal arched bar showing the
// sub-rooms (Drills / Run Lines / Stats / Journal).
import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const NAV_ROUTES = [
  { path: "/library", label: "Library" },
  { path: "/script",  label: "Script" },
  { path: "/record",  label: "Run Lines" },
  { path: "/confirm", label: "Kit" },
];
const ITEM_WIDTH = 110;
const SWIPE_THRESHOLD = 45;

const HIDE_ON = ["/upload", "/welcome", "/toolkit", "/import", "/room", "/building"];

export default function NavChrome({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const showNav = !HIDE_ON.some((p) => loc.pathname.startsWith(p));

  // Armed idx tracks which route the pointer-dot is over.
  const idxFromPath = () => {
    const i = NAV_ROUTES.findIndex((r) => loc.pathname.startsWith(r.path));
    return i >= 0 ? i : 0;
  };
  const [armedIdx, setArmedIdx] = useState(idxFromPath());
  const [isDragging, setIsDragging] = useState(false);
  const touchStartX = useRef<number | null>(null);

  function step(dir: number) {
    setArmedIdx((p) => Math.min(NAV_ROUTES.length - 1, Math.max(0, p + dir)));
  }

  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
    setIsDragging(true);
  }
  function onTouchEnd(e: React.TouchEvent) {
    setIsDragging(false);
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx < -SWIPE_THRESHOLD) step(1);
    else if (dx > SWIPE_THRESHOLD) step(-1);
    touchStartX.current = null;
  }

  function handleEnter() {
    const r = NAV_ROUTES[armedIdx];
    if (loc.pathname !== r.path) navigate(r.path);
  }

  // Sync armedIdx back to URL when route changes externally.
  if (NAV_ROUTES[armedIdx]?.path !== undefined &&
      !loc.pathname.startsWith(NAV_ROUTES[armedIdx].path)) {
    const fromPath = idxFromPath();
    if (fromPath !== armedIdx) {
      // Avoid setState during render
      window.queueMicrotask(() => setArmedIdx(fromPath));
    }
  }

  return (
    <>
      {/* Fixed top header — wrapped in .v2 so v2 styles apply (no legacy
          dark button bleed-through). */}
      <div className="v2" style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 20,
        margin: 0, padding: 0, minHeight: 0, background: "transparent",
      }}>
        <header
          style={{
            padding: "0.9rem 1rem",
            background: "var(--v2-cream, #F4F2EE)",
            borderBottom: "1px solid var(--v2-hairline, rgba(27,34,54,0.12))",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "var(--v2-sans, sans-serif)",
            textTransform: "uppercase",
            letterSpacing: "0.4em",
            fontSize: "0.7rem",
            fontWeight: 500,
            color: "var(--v2-ink, #1B2236)",
          }}
        >
          Off Book
        </header>
      </div>

      <div style={{ paddingTop: "3rem", paddingBottom: showNav ? "9rem" : "0" }}>
        {children}
      </div>

      {showNav && (
        <nav
          className="v2"
          style={{
            position: "fixed", left: 0, right: 0, bottom: 0,
            zIndex: 25,
            background: "var(--v2-cream, #F4F2EE)",
            borderTop: "1px solid var(--v2-hairline, rgba(27,34,54,0.12))",
            paddingBottom: "env(safe-area-inset-bottom)",
            margin: 0, padding: 0, minHeight: 0,
          }}
        >
          <div
            className="v2-arch"
            style={{ height: "150px", touchAction: "pan-y" }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div className="v2-arch-baseline" style={{ top: "60px" }} aria-hidden />

            {/* The pointer-dot */}
            <button
              className="v2-arch-pointer"
              style={{ top: "4px" }}
              onPointerUp={(e) => { e.stopPropagation(); handleEnter(); }}
              aria-label={`Go to ${NAV_ROUTES[armedIdx].label}`}
            >
              <span className="v2-arch-pointer-halo" aria-hidden />
              <span className="v2-arch-pointer-dot" aria-hidden />
              <span className="v2-arch-pointer-tick" aria-hidden />
            </button>

            <button
              className={"v2-arch-chev is-left" + (armedIdx === 0 ? " is-disabled" : "")}
              style={{ top: "44px" }}
              onPointerUp={(e) => { e.stopPropagation(); step(-1); }}
              aria-label="Previous"
            >‹</button>
            <button
              className={"v2-arch-chev is-right" + (armedIdx === NAV_ROUTES.length - 1 ? " is-disabled" : "")}
              style={{ top: "44px" }}
              onPointerUp={(e) => { e.stopPropagation(); step(1); }}
              aria-label="Next"
            >›</button>

            <div className="v2-arch-track-window" style={{ top: "38px" }}>
              <div
                className="v2-arch-track"
                style={{
                  marginLeft: `calc(50% - ${ITEM_WIDTH / 2}px)`,
                  transform: `translateX(${-(armedIdx * ITEM_WIDTH)}px)`,
                }}
              >
                {NAV_ROUTES.map((r, i) => {
                  const isArmed = i === armedIdx;
                  const distance = Math.abs(i - armedIdx);
                  const cls = isArmed ? "is-armed" : distance === 1 ? "is-near" : "is-far";
                  return (
                    <button
                      key={r.path}
                      className="v2-arch-slot"
                      style={{ width: `${ITEM_WIDTH}px`, flex: `0 0 ${ITEM_WIDTH}px` }}
                      onPointerUp={(e) => {
                        if (!isArmed && !isDragging) {
                          e.stopPropagation();
                          setArmedIdx(i);
                        } else if (isArmed && !isDragging) {
                          e.stopPropagation();
                          handleEnter();
                        }
                      }}
                    >
                      <span className={`v2-arch-label ${cls}`}>{r.label.toUpperCase()}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </nav>
      )}
    </>
  );
}
